import React, { useState } from "react";
import { Link } from "react-router-dom";
import { STATUSES, CHROME_IMAGES } from "@/data/mockData";
import { useAgents, usePauseAgent, useResumeAgent } from "@/data/agentsStore";
import {
  PageHeader,
  StatusDot,
  MaturityBadge,
  Pill,
  EditorialButton,
  StatusBadge,
} from "@/lib/editorial";
import { Pause, Play, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtCET } from "@/lib/utils";

export default function AgentsList() {
  const agents = useAgents();
  const pauseMut = usePauseAgent();
  const resumeMut = useResumeAgent();

  function togglePause(id) {
    const a = agents.find((x) => x.id === id);
    if (!a) return;
    if (a.active) {
      pauseMut.mutate(id, { onSuccess: () => toast(`${a.name} paused.`) });
    } else {
      resumeMut.mutate(id, { onSuccess: () => toast(`${a.name} resumed.`) });
    }
  }

  return (
    <div className="px-8 lg:px-14 py-12 max-w-[1500px]">
      <PageHeader
        eyebrow="Section 01 — Overview"
        tape="Agents"
        rest="under control"
        sub="Every LinkedIn account connected to the outreach engine. Vital signs at a glance — tick activity, queued messages, today's dispatch count."
        right={
          <EditorialButton as={Link} to="/admin/agents/new" variant="primary" testid="agents-new-btn">
            <Plus className="w-3.5 h-3.5" /> New agent
          </EditorialButton>
        }
        testId="agents-page-title"
      />

      {/* Editorial summary strip */}
      <div className="grid grid-cols-12 gap-6 mb-12">
        <div className="col-span-12 md:col-span-8 border-y border-[var(--rule-light)] py-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <SummaryStat label="Active agents" value={agents.filter((a) => a.active).length} />
          <SummaryStat label="Total contacts" value={agents.reduce((s, a) => s + a.stats.totalContacts, 0).toLocaleString()} />
          <SummaryStat label="Queued msgs" value={agents.reduce((s, a) => s + a.stats.queuedMsgs, 0)} />
          <SummaryStat label="Sent today" value={agents.reduce((s, a) => s + a.stats.sentToday, 0)} />
        </div>
        <div className="col-span-12 md:col-span-4 relative h-32 bg-[var(--ink)] overflow-hidden hidden md:block">
          <img
            src={CHROME_IMAGES.headerDecor}
            alt=""
            className="chrome-img w-full h-full object-cover opacity-95"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-transparent to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">
              Pipeline pulse
            </div>
            <div className="font-display italic text-white text-2xl mt-1">
              Engine is running smooth.
            </div>
          </div>
        </div>
      </div>

      {/* Agent cards */}
      <div className="space-y-6" data-testid="agents-list">
        {agents.length === 0 ? (
          <EmptyAgents />
        ) : (
          agents.map((a) => (
            <article
              key={a.id}
              className="border border-[var(--rule-light)] bg-white"
              data-testid={`agent-card-${a.id}`}
            >
              {/* Header */}
              <div className="flex items-center gap-4 px-6 py-5 border-b border-[var(--rule-light)] flex-wrap">
                <div className="flex items-center gap-3">
                  <StatusDot active={a.active} />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)]">
                    {a.active ? "Active" : "Paused"}
                  </span>
                </div>

                <Link
                  to={`/admin/agents/${a.id}`}
                  className="font-display text-[26px] leading-none hover:text-[var(--blue)] transition-colors"
                  data-testid={`agent-name-${a.id}`}
                >
                  {a.name}
                </Link>
                <span className="text-[11px] font-mono text-[var(--muted-light)]">#{a.id}</span>
                <MaturityBadge maturity={a.maturity} />

                <div className="ml-auto flex items-center gap-3">
                  {a.active ? (
                    <EditorialButton
                      variant="secondary"
                      onClick={() => togglePause(a.id)}
                      data-testid={`agent-pause-${a.id}`}
                    >
                      <Pause className="w-3.5 h-3.5" /> Pause
                    </EditorialButton>
                  ) : (
                    <EditorialButton
                      variant="secondary"
                      onClick={() => togglePause(a.id)}
                      data-testid={`agent-resume-${a.id}`}
                    >
                      <Play className="w-3.5 h-3.5" /> Resume
                    </EditorialButton>
                  )}
                  <Link
                    to={`/admin/agents/${a.id}`}
                    className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink)] hover:text-[var(--blue)] flex items-center gap-1.5"
                    data-testid={`agent-details-${a.id}`}
                  >
                    Details <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[var(--rule-light)] border-b border-[var(--rule-light)]">
                <CardStat label="Total contacts" value={a.stats.totalContacts.toLocaleString()} />
                <CardStat label="Queued msgs" value={a.stats.queuedMsgs} />
                <CardStat label="Sent today" value={a.stats.sentToday} />
                <CardStat
                  label="Last tick"
                  value={fmtCET(a.stats.lastTick)}
                />
              </div>

              {/* Status breakdown */}
              <div className="px-6 py-4 flex flex-wrap gap-2">
                {STATUSES.filter((s) => (a.statusBreakdown?.[s] ?? 0) > 0).map((s) => (
                  <span key={s} className="flex items-center gap-1.5">
                    <StatusBadge status={s} />
                    <span className="text-[11px] font-mono text-[var(--muted-light)]">
                      {a.statusBreakdown[s]}
                    </span>
                  </span>
                ))}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-1">
        {label}
      </div>
      <div className="font-display text-[36px] leading-none">{value}</div>
    </div>
  );
}

function CardStat({ label, value }) {
  return (
    <div className="px-6 py-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-2">
        {label}
      </div>
      <div className="font-display text-[26px] leading-none">{value}</div>
    </div>
  );
}

function EmptyAgents() {
  return (
    <div className="text-center py-20 border border-dashed border-[var(--rule-light)]">
      <p className="font-display italic text-3xl">No agents yet.</p>
      <p className="mt-3 text-[var(--muted-light)]">Connect a LinkedIn account to begin.</p>
      <Link
        to="/admin/agents/new"
        className="inline-flex mt-6 bg-[var(--blue)] text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em]"
      >
        Create your first agent
      </Link>
    </div>
  );
}
